// Today's insights: the few things in the trends most worth knowing, written
// by the AI once a day (server/brief.ts) — or, without AI, the strongest
// rule-based insights from domain/trends.ts.

import { today } from './time.ts';
import { metricChange, type InsightKind, type MetricId, type Trends } from './trends.ts';
import type { Millis } from './types.ts';

export interface DailyInsight {
  kind: InsightKind;
  title: string;
  detail: string;
  /** Where it comes from: "Your logs, last 14 days", "Camera bark detections + Dogs Trust guidance". */
  basedOn: string;
  route?: string;
}

export interface DailyInsights {
  date: string;
  headline: string;
  insights: DailyInsight[];
  via: 'ai' | 'rules';
  /** Which AI wrote it: the home's local model or Claude. */
  engine?: 'local' | 'claude';
  at?: Millis;
}

/** How an improvement reads in a sentence. */
const IMPROVED: Record<MetricId, string> = {
  outside: 'more trips outside',
  accidents: 'fewer accidents',
  holding: 'holding on longer',
  sleep: 'more sleep',
  night: 'longer nights',
  wakes: 'fewer night wakes',
  awake: 'shorter awake stretches',
  training: 'more training',
  social: 'more new experiences',
  spells: 'fewer wild spells',
};

/** The rule-based version: the top insights, as they are. */
export function templateInsights(t: Trends, now: Millis): DailyInsights {
  const top = t.insights.slice(0, 4);
  const insights = top.map((i) => ({
    kind: i.kind,
    title: i.title,
    detail: i.detail,
    basedOn: `${i.from === 'camera' ? 'Camera' : 'Your logs'}${i.sources?.length ? ` + ${[...new Set(i.sources.map((s) => s.label.split(' – ')[0]))].slice(0, 2).join(', ')} guidance` : ''}`,
    route: i.route,
  }));
  // "Better this week: toilet trips outside and night wakes. Focus: …"
  const better = t.metrics.filter((m) => metricChange(m) === 'better').slice(0, 2).map((m) => IMPROVED[m.id]);
  const focus = top.find((i) => i.kind !== 'win');
  const lower = (x: string) => x.charAt(0).toLowerCase() + x.slice(1);
  const headline = !top.length
    ? better.length
      ? `Better this week: ${better.join(' and ')}.`
      : 'Log a few days of naps, wees and meals and patterns start to show here.'
    : better.length
      ? `Better this week: ${better.join(' and ')}.${focus ? ` Focus: ${lower(focus.title)}.` : ''}`
      : top[0].title;
  return { date: today(now), headline, insights, via: 'rules' };
}

/** What gets sent to the AI: the numbers, rounded, without the series noise. */
export function trendsForAi(t: Trends): string {
  const r = (v?: number) => (v === undefined ? null : Math.round(v * 10) / 10);
  return JSON.stringify({
    metrics: t.metrics.map((m) => ({ id: m.id, label: m.label, unit: m.unit, last7: r(m.value), previous7: r(m.prev), better: m.better, daily: m.series.map(r), from: m.from })),
    insights: t.insights.map((i) => ({ kind: i.kind, title: i.title, detail: i.detail, from: i.from, guidance: i.sources?.map((s) => s.label) })),
  });
}
